import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, ArrowRight, Clock } from "lucide-react";
import api, { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { addOfferToCart } from "@/lib/offers";
import { useSeo } from "@/lib/seo";

export default function Offers() {
  useSeo({
    title: "Offers & Deals | Metros Fried Chicken Epsom",
    description: "Grab the latest Metros deals on fried chicken, burgers, wings and meal bundles. Save on takeaway collection and food delivery in Epsom, Surrey.",
    keywords: "fried chicken deals, meal deals Epsom, takeaway offers Epsom, food delivery Epsom, Metros fried chicken",
    path: "/offers",
    image: "https://mymetros.co.uk/logo.png",
  });
  const [offers, setOffers] = useState([]);
  const navigate = useNavigate();
  const { addItem, selectedBranch } = useCart();
  useEffect(() => { api.get(`/offers${selectedBranch ? `?branch_id=${selectedBranch.id}` : ""}`).then(({ data }) => setOffers(data)).catch(() => {}); }, [selectedBranch]);

  return (
    <div>
      <div className="bg-metros-black text-white grain px-4 md:px-6 py-8 md:py-10">
        <div className="max-w-7xl mx-auto">
          <p className="text-metros-red font-bold text-xs uppercase tracking-widest mb-1">Deals & Offers</p>
          <h1 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight">Save on your favourites</h1>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {offers.length === 0 ? (
          <div className="text-center py-20" data-testid="no-offers">
            <Tag className="mx-auto text-gray-300" size={40} />
            <p className="font-heading font-bold text-xl mt-3">No offers right now</p>
            <p className="text-muted-foreground text-sm">Check back soon for tasty deals.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((o) => {
              const saving = o.original_price - o.discounted_price;
              return (
                <div key={o.id} data-testid={`offer-${o.id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:-translate-y-1 transition-transform">
                  <div className="relative h-44">
                    {o.image ? <img src={o.image} alt={o.title} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gray-100" />}
                    <span className="absolute top-3 left-3 bg-metros-red text-white text-xs font-bold uppercase px-2 py-1 rounded">{o.type}</span>
                    {saving > 0 && <span className="absolute top-3 right-3 bg-metros-black text-white text-xs font-bold px-2 py-1 rounded">Save {money(saving)}</span>}
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-extrabold text-xl">{o.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{o.description}</p>
                    {o.discounted_price > 0 && (
                      <div className="flex items-center gap-2 mt-3">
                        <span className="line-through text-gray-400">{money(o.original_price)}</span>
                        <span className="font-heading font-extrabold text-metros-red text-2xl">{money(o.discounted_price)}</span>
                      </div>
                    )}
                    {o.expiry && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Clock size={12} /> Ends {new Date(o.expiry).toLocaleDateString()}</p>}
                    {o.product_ids && o.product_ids.length > 0 && (
                      <button data-testid={`offer-add-${o.id}`} onClick={() => addOfferToCart(o, { addItem, selectedBranch, navigate })}
                        className="mt-4 w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-3 font-bold flex items-center justify-center gap-2 active:scale-95 transition-all">
                        Add to basket
                      </button>
                    )}
                    <button data-testid={`get-deal-${o.id}`} onClick={() => navigate((o.product_ids && o.product_ids.length) ? `/menu?offer_id=${encodeURIComponent(o.id)}` : `/menu?category=${encodeURIComponent(o.type)}`)}
                      className="mt-2 w-full bg-white border border-gray-200 hover:border-metros-red text-metros-black rounded-full py-3 font-bold flex items-center justify-center gap-2 active:scale-95 transition-all">
                      Get Deal <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
